// =============================================================================
// Painel White Label — Financeiro
// Consolida 4 abas (Visão geral, Assinaturas, Cobrança, Add-ons) com filtro
// global de período (MonthNavigator) e filtro global de status quando aplicável.
//
// RELEASE:
//   2026-07-22 v1.2.0 - Aba Add-ons refatorada (padrão Assinaturas/Cobrança):
//                       KPIs premium, gráficos evolutivos e listagem paginada.
//                       Header ganha MonthNavigator global para filtrar por
//                       período todas as abas que dependem do mês.
//   2026-07-22 v1.1.0 - Aba Assinaturas reformulada (KPIs + listagem premium).
// =============================================================================
import { useState } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { CreditCard, Package, LayoutDashboard, Receipt } from "lucide-react";
import { format } from "date-fns";
import { WlFinancialOverviewTab } from "@/components/wl/financial/WlFinancialOverviewTab";
import { WlSubscriptionKpiGrid } from "@/components/wl/financial/WlSubscriptionKpiGrid";
import { WlSubscriptionsListCard } from "@/components/wl/financial/WlSubscriptionsListCard";
import { WlBillingTab } from "@/components/wl/financial/WlBillingTab";
import { WlAddonsTab } from "@/components/wl/financial/WlAddonsTab";
import {
  WlFinancialStatusFilter,
  WL_FINANCIAL_STATUS_DEFAULT,
} from "@/components/wl/financial/WlFinancialStatusFilter";
import { MonthNavigator } from "@/components/financial/MonthNavigator";

/**
 * Página raiz do módulo Financeiro (Painel WL).
 *
 * Estado global controlado aqui:
 * - `referenceMonth` (Date | null): mês selecionado no MonthNavigator do header.
 *   Quando null, os componentes consideram o mês corrente.
 * - `overviewStatuses` (string[]): filtro multi-select de status (Visão geral +
 *   KPIs de Assinaturas).
 *
 * Ambos são propagados às abas via props — mantém uma única fonte de verdade.
 */
export default function WlFinancial() {
  // Aba ativa (controlada) para condicionar filtros do header.
  const [activeTab, setActiveTab] = useState<string>("overview");

  // Filtro global de status (Visão geral + KPIs de Assinaturas).
  const [overviewStatuses, setOverviewStatuses] = useState<string[]>([
    ...WL_FINANCIAL_STATUS_DEFAULT,
  ]);

  // Filtro global de período (MonthNavigator). null = mês corrente.
  const [referenceMonthDate, setReferenceMonthDate] = useState<Date | null>(null);

  /** Formato ISO (yyyy-MM-dd) esperado pelas RPCs. Null preserva default backend. */
  const referenceMonthISO = referenceMonthDate
    ? format(referenceMonthDate, "yyyy-MM-01")
    : null;

  // O filtro de status é útil apenas em Visão geral e Assinaturas.
  const showStatusFilter = activeTab === "overview" || activeTab === "subscriptions";

  // O filtro de período é útil em todas as abas exceto Assinaturas listagem
  // (que já tem filtros próprios). Mantemos visível em overview/billing/addons.
  const showMonthNavigator =
    activeTab === "overview" ||
    activeTab === "billing" ||
    activeTab === "addons" ||
    activeTab === "subscriptions";

  return (
    <div className="p-6 space-y-6">
      {/* Header: título + MonthNavigator + filtro global de status */}
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-3">
          <CreditCard className="h-6 w-6 text-primary" />
          <h1 className="text-2xl font-bold">Financeiro</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {showMonthNavigator && (
            <MonthNavigator
              currentMonth={referenceMonthDate ?? new Date()}
              onChange={setReferenceMonthDate}
              onClear={() => setReferenceMonthDate(null)}
            />
          )}
          {showStatusFilter && (
            <WlFinancialStatusFilter
              value={overviewStatuses}
              onChange={setOverviewStatuses}
            />
          )}
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        {/* Tabs estilo "underline" — linha inferior + sem fundo cinza */}
        <TabsList className="bg-transparent border-b h-auto p-0 gap-6 w-full justify-start rounded-none">
          <TabsTrigger
            value="overview"
            className="rounded-none bg-transparent px-0 pb-3 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:font-semibold text-muted-foreground data-[state=active]:text-foreground"
          >
            <LayoutDashboard className="h-4 w-4 mr-2" /> Visão geral
          </TabsTrigger>
          <TabsTrigger
            value="subscriptions"
            className="rounded-none bg-transparent px-0 pb-3 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:font-semibold text-muted-foreground data-[state=active]:text-foreground"
          >
            <CreditCard className="h-4 w-4 mr-2" /> Assinaturas
          </TabsTrigger>
          <TabsTrigger
            value="billing"
            className="rounded-none bg-transparent px-0 pb-3 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:font-semibold text-muted-foreground data-[state=active]:text-foreground"
          >
            <Receipt className="h-4 w-4 mr-2" /> Cobrança
          </TabsTrigger>
          <TabsTrigger
            value="addons"
            className="rounded-none bg-transparent px-0 pb-3 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:font-semibold text-muted-foreground data-[state=active]:text-foreground"
          >
            <Package className="h-4 w-4 mr-2" /> Add-ons
          </TabsTrigger>
        </TabsList>

        {/* ============== VISÃO GERAL ============== */}
        <TabsContent value="overview" className="space-y-4 pt-4">
          <WlFinancialOverviewTab statuses={overviewStatuses} />
        </TabsContent>

        {/* ============== ASSINATURAS ============== */}
        <TabsContent value="subscriptions" className="space-y-4 pt-4">
          <WlSubscriptionKpiGrid statuses={overviewStatuses} />
          <WlSubscriptionsListCard />
        </TabsContent>

        {/* ============== COBRANÇA ============== */}
        <TabsContent value="billing" className="space-y-4 pt-4">
          <WlBillingTab />
        </TabsContent>

        {/* ============== ADD-ONS (v1.2.0 — nova UI padronizada) ============== */}
        <TabsContent value="addons" className="space-y-4 pt-4">
          <WlAddonsTab referenceMonth={referenceMonthISO} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
